/**
 * @file 积分充值弹窗
 * @description 一屏式布局：左侧积分档位（PackageList），右侧支付二维码（PaymentPanel）。
 *              顶部为用户信息条（UserHeader）。
 *              支付会话生命周期由 usePaymentSession 统一管理，
 *              关闭弹窗时直接调用 session.cancel() 中止会话，避免悬挂订单。
 */
import {useCallback, useEffect, useRef, useState, useMemo} from 'react';
import PackageList from './PackageList';
import PaySuccessModal from './PaySuccessModal';
import UserHeader from './UserHeader';
import {usePointsRechargeTracking} from './hooks/usePointsRechargeTracking';
import {PACKAGE_ID_ENUMS} from './types';
import styles from './index.module.less';
import Modal from '@/components/common/Modal';
import {createCx} from '@/utils/cx';
import {useModalStore} from '@/store/useModalStore';
import {invalidatePointsSummary} from '@/hooks/points/usePersonalPoints';
import type {Order} from '@/services/commerce/entities/payment';
import type {PointsSku} from '@/services/commerce/points/interface';
import {createPointsOrder} from '@/services/commerce/payment';
import {usePaymentSession, PaymentPanel} from '@/commerce/payment';
import {usePaymentCheck} from '@/commerce/payment/hooks/usePaymentCheck';
import type {PaymentPanelEvent} from '@/commerce/payment';
import {TrackEvents} from '@/services/tracking/constant';
import {useMemberExtShowLog} from '@/hooks/memberLogExt';

const cx = createCx(styles);

/** 未充值过：所有 SKU 均处于 isFirstCharge === true 状态 */
const isNewUser = (skus: PointsSku[]): boolean =>
    skus.length > 0 && skus.every((sku) => sku.isFirstCharge);

/** 根据 skuId 在列表中的顺序映射为 package_id 枚举 */
const getPackageId = (skus: PointsSku[], skuId: PointsSku['skuId']): string => {
    const index = skus.findIndex((sku) => sku.skuId === skuId);
    return PACKAGE_ID_ENUMS[index] ?? '';
};

const TIP_FOR_NEW_USER =
    '温馨提示：首充加赠仅限新用户首次充值，仅一次机会。会员可享去水印、加速队列、专属模型等权益，仅购买积分无法获得相应会员权益。';

const TIP_FOR_EXISTING_USER =
    '温馨提示：会员可享去水印、加速队列、专属模型等权益，仅购买积分无法获得相应会员权益。';

const PointsRechargeModal: React.FC = () => {
    const open = useModalStore((s) => s.pointsRechargeModalOpen);
    const toggleOpen = useModalStore((s) => s.togglePointsRechargeModal);
    const setSource = useModalStore((s) => s.setPointsRechargeModalSource);

    const [selectedSku, setSelectedSku] = useState<PointsSku | null>(null);
    const [skuList, setSkuList] = useState<PointsSku[]>([]);
    // 支付成功结果：非空即展示支付成功弹窗
    const [paySuccessResult, setPaySuccessResult] = useState<Order['sku'] | null>(null);
    // 边界case，进入权益下发中状态 展示提示
    const [showRightsPendingTip, setShowRightsPendingTip] = useState(false);
    // 价格校验不一致时用于触发 PackageList 重新拉取
    const [skuRefreshKey, setSkuRefreshKey] = useState(0);
    // 仅在用户主动切换 SKU 时进行价格校验；初始化 auto-select 与价格失效后刷新的 auto-select 都跳过校验.否则刷新后再校验会陷入无限循环
    const shouldCheckPriceRef = useRef(false);

    // 支付拦截：校验当前显示价格与服务端最新价格是否一致
    const {mutateAsync: checkPayment} = usePaymentCheck();

    // 打点：注入公参
    const {sendClickLog, sendShowLog} = usePointsRechargeTracking({skuList});

    // 支付会话：下单 + 轮询 + 状态机
    const session = usePaymentSession({
        createOrder: createPointsOrder,
        onSuccess: (result, rightsIssued) => {
            // 支付成功弹窗曝光打点
            sendShowLog({
                value: TrackEvents.POINT_SUCCESS_PAY,
                // eslint-disable-next-line camelcase
                ext: {package_id: getPackageId(skuList, result.skuId)},
            });
            // 关闭当前充值弹窗，切换到支付成功弹窗
            toggleOpen(false);
            setPaySuccessResult(result);
            if (rightsIssued) {
                // 权益已下发，立即刷新积分余额
                void invalidatePointsSummary();
            } else {
                // 权益尚未下发，展示 tip
                setShowRightsPendingTip(true);
            }
        },
        onRightsIssued: (result) => {
            setPaySuccessResult(result);
            setShowRightsPendingTip(false);
            void invalidatePointsSummary();
        },
    });

    // 弹窗展现打点：SKU 加载完成后上报，保证 is_first_charge 已就绪
    useMemberExtShowLog(
        open &&
            skuList.length > 0 && {
                value: TrackEvents.POINT_CREDITS_PURCHASE_POP,
            },
        sendShowLog,
    );

    // 选中 SKU 变化时 价格校验与下单并行发起
    // 仅在用户主动切换后做一次价格校验：若校验失败则刷新档位列表；刷新后自动选中不再触发校验，直到用户再次主动切换
    useEffect(() => {
        if (!open || !selectedSku?.skuId) {
            return () => {};
        }
        let canceled = false;
        // 下单始终执行，保证二维码能及时出现
        session.switchSku({orderType: 'points', skuId: selectedSku.skuId});

        // 刷新完sku列表后
        if (!shouldCheckPriceRef.current) {
            return () => {
                canceled = true;
            };
        }
        shouldCheckPriceRef.current = false;
        void checkPayment({
            skuId: selectedSku.skuId,
            payAmount: selectedSku.sellPrice,
        })
            .then((res) => {
                if (canceled || res.pass) {
                    return;
                }
                // 价格已变化：刷新档位列表；刷新后不会再触发 check，等待用户下次主动切换
                setSkuRefreshKey((k) => k + 1);
            })
            .catch((error: unknown) => {
                if (canceled) {
                    return;
                }
                console.error('价格校验失败:', error);
            });
        return () => {
            canceled = true;
        };
    }, [selectedSku?.skuId, open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open) {
            setSelectedSku(null);
            shouldCheckPriceRef.current = false;
            // 弹窗关闭时：仅非 success 态才 cancel（success 态由成功弹窗关闭后处理）
            if (!session.isSuccess) {
                session.cancel();
            }
            // 清理打点来源，避免下一次打开时误用
            setSource(null);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // 用户主动点击 SKU 时上报点击埋点，并标记本次切换需要做价格校验
    const handleUserSelectSku = useCallback(
        (sku: PointsSku) => {
            shouldCheckPriceRef.current = true;
            sendClickLog({
                value: TrackEvents.POINT_PLAN_SELECT,
                // eslint-disable-next-line camelcase
                ext: {package_id: getPackageId(skuList, sku.skuId)},
            });
        },
        [sendClickLog, skuList],
    );

    // 协议文本
    const agreement = useMemo(() => {
        return (
            <div className={styles.agreement}>
                <span>开通即同意</span>
                <a href="/membershipAgreement" target="_blank" rel="noopener noreferrer">
                    《会员与积分服务协议》
                </a>
            </div>
        );
    }, []);

    const handleCancel = useCallback(() => {
        toggleOpen(false);
    }, [toggleOpen]);

    const handlePaySuccessClose = useCallback(() => {
        // 如果权益未下发（用户提前关了成功弹窗），延迟 500ms 兜底刷新
        if (session.isPendingRights) {
            setTimeout(() => void invalidatePointsSummary(), 500);
        }
        setPaySuccessResult(null);
        setShowRightsPendingTip(false);
        session.cancel();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const tip = isNewUser(skuList) ? TIP_FOR_NEW_USER : TIP_FOR_EXISTING_USER;

    // PaymentPanel 事件处理
    const handlePaymentEvent = useCallback(
        (event: PaymentPanelEvent) => {
            // 二维码重试展现埋点
            if (event.type === 'retry-show') {
                sendShowLog({
                    page: TrackEvents.POINT_CREDITS_PURCHASE_POP,
                    value: TrackEvents.POINT_QRCODE_RETRY,
                    ext: {
                        // eslint-disable-next-line camelcase
                        package_id: selectedSku?.skuId
                            ? getPackageId(skuList, selectedSku.skuId)
                            : '',
                    },
                });
            }
        },
        [selectedSku?.skuId, sendShowLog, skuList],
    );

    return (
        <>
            <Modal
                open={open}
                onCancel={handleCancel}
                destroyOnHidden
                size="lg"
                className={styles.modal}
                title={null}
                footer={null}
            >
                <div className={cx('container')}>
                    <div className={cx('left')}>
                        <UserHeader />
                        <div className={cx('packageWrapper')}>
                            <PackageList
                                selectedSkuId={selectedSku?.skuId ?? null}
                                onSelect={setSelectedSku}
                                onUserSelect={handleUserSelectSku}
                                onSkusLoaded={setSkuList}
                                refreshKey={skuRefreshKey}
                            />
                        </div>
                        <div className={cx('tip')}>{tip}</div>
                    </div>
                    <div className={cx('right')}>
                        <PaymentPanel
                            className={styles.paymentPanelContainer}
                            session={session}
                            qrSize={172}
                            agreement={agreement}
                            onEvent={handlePaymentEvent}
                        />
                    </div>
                </div>
            </Modal>
            {/* 支付成功弹窗 */}
            <PaySuccessModal
                open={!!paySuccessResult}
                result={paySuccessResult}
                showRightsPendingTip={showRightsPendingTip}
                onClose={handlePaySuccessClose}
            />
        </>
    );
};

export default PointsRechargeModal;
